import React, { useEffect, useMemo, useState } from "react";

import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import AutoSuggest from "../../../../../../../../components/Form/AutoSuggest";
import Input from "../../../../../../../../components/Form/Input";
import Select from "../../../../../../../../components/Form/Select";
import Modal from "../../../../../../../../components/Generic/Modal";
import { useAppState } from "../../../../../../../../context/StateContext";
import { Currency } from "../../../../../../../../models/currency";
import { Status } from "../../../../../../../../util/enums";
import { formatCurrency } from "../../../../../../../../util/prices";
import { currencyOptions } from "./currencyOptions";
import useStyles from "./styles";

const MAX_DECIMAL_POINTS = 5;
const MIN_DECIMAL_POINTS = 0;

interface Props {
  currencyToUpdate?: Currency;
  open: boolean;
  hasError?: boolean;
  isLoading: boolean;
  closeModal: () => void;
  updateCurrency?: (currency: Currency) => void;
  createCurrency?: (currency: Currency) => void;
}

const CurrencyModal = ({
  currencyToUpdate,
  open,
  hasError,
  isLoading,
  closeModal,
  updateCurrency,
  createCurrency,
}: Props) => {
  const classes = useStyles();
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const [currencyName, setCurrencyName] = useState("");
  const [currencySymbol, setCurrencySymbol] = useState("");
  const [currencyDecimalPoints, setCurrencyDecimalPoints] = useState("");
  const [currencyCode, setCurrencyCode] = useState("");
  const [decimalSeparator, setDecimalSeparator] = useState("");
  const [thousandSeparator, setThousandSeparator] = useState("");
  const [status, setStatus] = useState<string>(Status.ACTIVE);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const separators = useMemo(() => {
    return [
      {
        label: translate("usePoint"),
        value: ".",
        key: "usePoint",
      },
      {
        label: translate("useComma"),
        value: ",",
        key: "useComma",
      },
    ];
  }, [translate]);

  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;

  const schema = yup.object().shape({
    currencyName: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired"))
      .test("empty-check", translate("thisFieldIsRequired"), (currencyName) =>
        currencyName ? currencyName !== "" : false
      ),
    currencySymbol: yup.string(),
    currencyDecimalPoints: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired"))
      .test("empty-check", translate("thisFieldIsRequired"), (currencyDecimalPoints) =>
        currencyDecimalPoints ? currencyDecimalPoints !== "" : false
      ),
    decimalSeparator: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired"))
      .test("empty-check", translate("thisFieldIsRequired"), (decimalSeparator) =>
        decimalSeparator ? decimalSeparator !== "" : false
      ),
    thousandSeparator: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired"))
      .test("empty-check", translate("thisFieldIsRequired"), (thousandSeparator) =>
        thousandSeparator ? thousandSeparator !== "" : false
      ),
    currencyCode: yup.string(),
    status: yup.string(),
  });

  const clearFields = () => {
    setCurrencyName("");
    setCurrencySymbol("");
    setCurrencyDecimalPoints("");
    setCurrencyCode("");
    setDecimalSeparator("");
    setThousandSeparator("");
  };

  const isValid = (currency: Currency) => {
    try {
      schema.validateSync(
        {
          currencyName: currency.name,
          currencySymbol: currency.symbol,
          currencyDecimalPoints: currency.decimalPoints,
          currencyCode: currency.code,
          decimalSeparator: currency.decimalSeparator,
          thousandSeparator: currency.thousandSeparator,
        },
        { abortEarly: false }
      );
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    }
  };

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    if (currencyToUpdate) {
      const object = {
        ...currencyToUpdate,
        name: currencyName,
        symbol: currencySymbol,
        code: currencyCode,
        decimalPoints: currencyDecimalPoints,
        status,
        decimalSeparator,
        thousandSeparator,
      };

      if (isValid(object)) {
        if (updateCurrency) updateCurrency(object);
      }
    } else {
      const object = {
        name: currencyName,
        symbol: currencySymbol,
        code: currencyCode,
        decimalPoints: currencyDecimalPoints,
        status,
        companyId: currentUser?.companyId,
        decimalSeparator,
        thousandSeparator,
      };

      if (isValid(object)) {
        if (createCurrency) createCurrency(object);
      }
    }
  };

  useEffect(() => {
    if (currencyToUpdate) {
      setCurrencyName(currencyToUpdate.name || "");
      setCurrencySymbol(currencyToUpdate.symbol || "");
      setCurrencyCode(currencyToUpdate.code || "");
      setCurrencyDecimalPoints(currencyToUpdate.decimalPoints || "0");
      setStatus(currencyToUpdate.status || "");
      setDecimalSeparator(currencyToUpdate.decimalSeparator || "");
      setThousandSeparator(currencyToUpdate.thousandSeparator || "");
    }
  }, [currencyToUpdate]);

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      clearFields();
      closeModal();
    }
  }, [hasError]);

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      const object = {
        name: currencyName,
        symbol: currencySymbol,
        code: currencyCode,
        decimalPoints: currencyDecimalPoints,
        status,
        companyId: currentUser?.companyId,
        decimalSeparator,
        thousandSeparator,
      };

      isValid(object);
    }
  }, [
    hasFormBeenSubmitted,
    currencyName,
    currencySymbol,
    currencyCode,
    currencyDecimalPoints,
    status,
    decimalSeparator,
    thousandSeparator,
  ]);

  const getCurrencyView = () => {
    if (!decimalSeparator || !thousandSeparator) return `100`;
    const VALUE = 10000.0;

    let cases = 0;
    const decimal = decimalSeparator || ".";
    const thousand = thousandSeparator || ",";

    if (currencyDecimalPoints && !Number.isNaN(Number(currencyDecimalPoints))) {
      cases = Number(currencyDecimalPoints) || 0;
    }
    const toFixed = VALUE.toFixed(cases);
    const currency = String(formatCurrency(VALUE)).replace("$", "");
    const first = currency.split(".")[0].replace(",", thousand);
    const second = toFixed.split(".")[1];

    return `${first}${second ? decimal : ""}${second || ""}`;
  };

  return (
    <Modal
      data-cy="currencyModal"
      open={open}
      handleClose={closeModal}
      title={currencyToUpdate ? translate("editCurrency") : translate("createCurrency")}
      labelStatus={translate("status")}
      showStatusField={true}
      statusFieldProps={{
        label: translate("currencyStatus"),
        value: status,
        dataCy: "selectStatus",
        placeholder: translate("selectAnOption"),
        handleChange: (value) => setStatus(value),
      }}
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        dataCySave: "submitChanges",
        onCancel: () => {
          clearFields();
          closeModal();
        },
        saveDisabled: isLoading,
        loading: isLoading,
        textSave: currencyToUpdate ? translate("update") : translate("create"),
        onSave: handleSubmit,
      }}
      className={classes.modalWidth}
    >
      <section className={classes.modalSection}>
        <Grid container direction="row" spacing={3}>
          <Grid item md={3} lg={3}>
            <AutoSuggest
              dataCy="currencyCode"
              placeholder={translate("aud")}
              name="currencyCode"
              disabled={isLoading}
              label={translate("code")}
              error={!!getErrorByField("currencyCode")}
              errorMessage={getErrorByField("currencyCode")}
              options={Object.keys(currencyOptions)}
              inputValue={currencyCode}
              onChange={(newCurrencySymbol: keyof typeof currencyOptions) => {
                const name = currencyOptions[newCurrencySymbol];

                setCurrencyCode(newCurrencySymbol);
                setCurrencyName((prev) => name || prev);
              }}
            />
          </Grid>

          <Grid item md={4} lg={4}>
            <Input
              disabled={isLoading}
              name="currencySymbol"
              label={translate("displayUnit")}
              placeholder={translate("a")}
              error={!!getErrorByField("currencySymbol")}
              errorMessage={getErrorByField("currencySymbol")}
              value={currencySymbol}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setCurrencySymbol(event.target.value)
              }
            />
          </Grid>

          <Grid item md={3} lg={3}>
            <Input
              disabled={isLoading}
              name="currencyDecimalPoints"
              label={translate("decimalPoints")}
              placeholder="2"
              type="number"
              error={!!getErrorByField("currencyDecimalPoints")}
              errorMessage={getErrorByField("currencyDecimalPoints")}
              errorBreakLine={false}
              max={MAX_DECIMAL_POINTS}
              min={MIN_DECIMAL_POINTS}
              value={currencyDecimalPoints}
              onChange={(e) => {
                const number = Number(e.target.value);

                if (Number.isNaN(number)) {
                  setCurrencyDecimalPoints("0");

                  return;
                }

                if (number > MAX_DECIMAL_POINTS)
                  setCurrencyDecimalPoints(MAX_DECIMAL_POINTS.toString());
                if (number < MIN_DECIMAL_POINTS)
                  setCurrencyDecimalPoints(MIN_DECIMAL_POINTS.toString());
                if (number <= MAX_DECIMAL_POINTS && number >= MIN_DECIMAL_POINTS)
                  setCurrencyDecimalPoints(e.target.value);
              }}
            />
          </Grid>
        </Grid>

        <Grid container direction="row" spacing={2} className={classes.mTop16}>
          <Grid item md={4} lg={4}>
            <Input
              disabled={isLoading}
              name="currencyName"
              label={translate("fullName")}
              placeholder={translate("australianDollar")}
              error={!!getErrorByField("currencyName")}
              errorMessage={getErrorByField("currencyName")}
              value={currencyName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrencyName(e.target.value)}
            />
          </Grid>

          <Grid item md={4} lg={4}>
            <Select
              label={translate("decimalSeparator")}
              disabled={isLoading}
              dataCy="decimalSeparator"
              name="decimalSeparator"
              placeholder={translate("selectAnOption")}
              error={!!getErrorByField("decimalSeparator")}
              errorMessage={getErrorByField("decimalSeparator")}
              displayEmpty
              defaultValue={""}
              value={decimalSeparator}
              onChange={(e: SelectChangeEvent<any>) => setDecimalSeparator(e.target.value)}
            >
              {separators?.map((separator) => (
                <MenuItem data-cy={separator.key} key={separator.value} value={separator.value}>
                  {separator.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item md={4} lg={4}>
            <Select
              label={translate("thousandSeparator")}
              disabled={isLoading}
              dataCy="thousandSeparator"
              name="thousandSeparator"
              placeholder={translate("selectAnOption")}
              error={!!getErrorByField("thousandSeparator")}
              errorMessage={getErrorByField("thousandSeparator")}
              displayEmpty
              defaultValue={""}
              value={thousandSeparator}
              onChange={(e: SelectChangeEvent<any>) => setThousandSeparator(e.target.value)}
            >
              {separators?.map((separator) => (
                <MenuItem data-cy={separator.key} key={separator.value} value={separator.value}>
                  {separator.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>

        <Grid container direction="row" spacing={2} className={classes.mTop16}>
          <Grid item md={6} lg={6}>
            <p className={classes.currencyViewTitle}>{translate("currencyView")}</p>

            <Input
              name="viewCurrency"
              leftIcon={<span>{currencySymbol}</span>}
              rightIcon={<span>{currencyCode}</span>}
              value={getCurrencyView()}
              disabled={true}
            />
          </Grid>
        </Grid>
      </section>
    </Modal>
  );
};

export default CurrencyModal;
